/* @jsx h */
import { defineComponent, h, computed, ref, Ref } from "vue";
import { Cart, TableStatus } from "@lemmonhq/types";
import { useCheckout, useShop, useAuth } from "@app/vue/providers/index";
import { useRouter } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";
import { useCart } from "@lemmonhq/orders";

interface CartActionsProps {
  selectedTable: Ref<TableStatus | undefined | null>;
  cart: Cart;
  onClear: () => void;
  onAddNote: () => void;
  onSendToTable: () => void;
}

export const CartActions = defineComponent<CartActionsProps>({
  name: "CartActions",
  props: ["cart", "onClear", "onAddNote", "onSendToTable", "selectedTable"],
  setup(props) {
    const auth = useAuth();
    const checkout = useCheckout();
    const { t } = useLocalization();
    const cartAPI = useCart();
    const shop = useShop();

    const total = computed(() => {
      return props.cart.items.reduce(
        (sum, item) =>
          sum +
          (parseFloat(item.price) + (parseFloat(item.bundlePrice || "") || 0)) *
            item.quantity,
        0,
      );
    });
    const router = useRouter();

    const hasItems = computed(() => props.cart.items.length > 0);

    return () => (
      <div class="border-t p-4 [background:var(--pos-panel-bg)] rounded">
        <div class="flex justify-between mb-4 text-accent">
          <span class="text-2xl font-bold">Total</span>
          <span class="text-2xl">
            {auth.user.value?.restaurant.currency || "CHF"}{" "}
            {total.value.toFixed(2)}
          </span>
        </div>
        <div class="grid grid-cols-2 gap-2">
          <>
            <button
              class="w-full btn btn-neutral"
              disabled={!hasItems.value || !!props.selectedTable.value}
              onClick={() => {
                checkout.setCheckout({
                  payload: { type: "cart", object: props.cart },
                  serviceMethod: "takeaway",
                });
                checkout.show({
                  onCancel() {},
                  onSuccess() {
                    props.onClear();
                    shop.refreshOrders();
                    router.push("/app/pos");
                  },
                });
              }}
            >
              {t("pos.cart.take_away_button")}
            </button>
            <button
              class="w-full btn btn-secondary"
              disabled={!hasItems.value}
              onClick={props.onAddNote}
            >
              {t("pos.cart.add_a_note_button")}
            </button>
          </>
        </div>
        <div class="grid grid-cols-2 gap-2 mt-2">
          <button
            data-training="pos.cart.pay-now"
            class="w-full btn btn-secondary"
            disabled={!hasItems.value || !!props.selectedTable.value}
            onClick={() => {
              checkout.setCheckout({
                payload: { type: "cart", object: props.cart },
                serviceMethod: "dine-in",
              });
              checkout.show({
                onCancel() {},
                onSuccess() {
                  props.onClear();
                  shop.refreshOrders();
                  router.push("/app/pos");
                },
              });
            }}
          >
            <span class="flex items-center gap-2">
              {t("pos.cart.pay_button")}
            </span>
          </button>
          <button
            data-training="pos.cart.send-to-table"
            class="w-full btn btn-primary"
            disabled={!hasItems.value}
            onClick={props.onSendToTable}
          >
            <span class="flex items-center gap-2">
              {t("pos.cart.send to table_button")}
            </span>
          </button>
        </div>
      </div>
    );
  },
});
